import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CancelClassDialog } from "@/components/teacher/schedule/CancelClassDialog";
import { EditClassDialog } from "@/components/teacher/schedule/EditClassDialog";
import { ChevronDown, XCircle, Pencil } from "lucide-react";
import type { WeeklyItem, ClassTemplateEvent } from "@/types/schedule";
import { canAttendFromHours } from "@/utils/scheduleProjection";

export function OccurrenceRow({ item, teacherCode, events, onRefresh, isNew }: {
  item: WeeklyItem;
  teacherCode: string;
  events?: ClassTemplateEvent[];
  onRefresh?: () => void;
  isNew?: boolean;
}) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const students = item.students || [];

  const formatHoursNumber = (val: number | null | undefined) => {
    if (val == null) return '-';
    const r1 = Math.round(val * 10) / 10;
    return Number.isInteger(r1) ? r1.toFixed(0) : r1.toFixed(1);
  };
  const formatHoursFromMinutes = (min: number | null | undefined) => {
    if (min == null) return '-';
    const hours = min / 60;
    const r1 = Math.round(hours * 10) / 10;
    return Number.isInteger(r1) ? r1.toFixed(0) : r1.toFixed(1);
  };

  const isLeave = item.status === 'STUDENT_LEAVE' || item.status === 'TEACHER_LEAVE';
  const isAbsent = item.status === 'STUDENT_ABSENT';

  // Filter events for this class+date
  const relatedEvents = (events || []).filter(
    (e) => e.class_template_id === item.fixed_job_id && e.event_date === item.date
  );

  const cardClasses = [
    'flex flex-col gap-2 border rounded p-3 relative overflow-hidden',
    isNew ? 'border-green-400 bg-green-50' : '',
    isAbsent ? 'border-red-300 bg-red-50' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      {isLeave && (
        <div className="pointer-events-none select-none absolute inset-0 flex items-center justify-center">
          <span className="text-red-500 opacity-20 text-3xl font-extrabold tracking-widest -rotate-12">
            {item.status}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="min-w-28 font-medium">{item.start_time.slice(0,5)} - {item.end_time.slice(0,5)}</div>
          <div className="flex flex-col">
            <div className="font-semibold flex items-center gap-2">
              {item.subject} · {item.location}
              {isNew && (
                <Badge variant="success" className="text-xs">ตารางใหม่</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>สถานะ: {item.status}</span>
            </div>
            {students.length > 0 && (
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                {students.map((s) => {
                  const availableMinutes = (s.hours ?? 0) * 60 - (s.active_hold_minutes ?? 0);
                  const beforeHours = s.before_hours;
                  const deductHours = s.deduct_hours;
                  const afterHours = s.after_hours;
                  const hasProjection = beforeHours != null && deductHours != null && afterHours != null;
                  const canAttend = s.can_attend ?? (hasProjection ? canAttendFromHours(beforeHours, deductHours) : true);
                  return (
                    <div key={s.code} className="flex items-center justify-between">
                      <span>{`${s.code} น้อง${s.nickname ?? ''} ${s.first_name ?? ''} ${s.last_name ?? ''}`.trim()}</span>
                      {hasProjection ? (
                        <span className={canAttend === false ? "text-red-600 font-medium" : ""}>
                          ก่อนเรียน {formatHoursNumber(beforeHours)} ชม · หัก {formatHoursNumber(deductHours)} ชม · หลังเรียน {formatHoursNumber(afterHours)} ชม
                          {canAttend === false ? " · เรียนไม่ได้" : ""}
                        </span>
                      ) : (
                        <span>
                          เหลือ {formatHoursNumber(s.hours)} ชม · ใช้ได้ {formatHoursFromMinutes(Math.max(availableMinutes, 0))} ชม
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm">
                ขอแก้ไข <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setCancelOpen(true)}>
                <XCircle className="mr-2 h-4 w-4" />
                แจ้งยกเลิกคลาส
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setEditOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                แก้ไขข้อมูลคลาส
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Show existing events */}
      {relatedEvents.length > 0 && (
        <div className="space-y-1">
          {relatedEvents.map((ev) => {
            const statusClasses = ev.status === 'APPROVED'
              ? 'border-green-300 bg-green-50 text-green-700'
              : ev.status === 'REJECTED'
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-amber-300 bg-amber-50 text-amber-700';
            const typeLabel = ev.event_type === 'CANCEL' ? 'ยกเลิกคลาส' : 'แก้ไขข้อมูลคลาส';
            return (
              <div key={ev.id} className={`flex justify-between items-center p-2 rounded border text-xs ${statusClasses}`}>
                <span>คำขอ: {typeLabel}{ev.note ? ` — ${ev.note}` : ''}</span>
                <Badge variant="outline">{ev.status}</Badge>
              </div>
            );
          })}
        </div>
      )}

      <CancelClassDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        classTemplateId={item.fixed_job_id}
        eventDate={item.date}
        teacherCode={teacherCode}
        onSuccess={onRefresh}
      />
      <EditClassDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        classTemplateId={item.fixed_job_id}
        eventDate={item.date}
        teacherCode={teacherCode}
        defaultSubject={item.subject}
        onSuccess={onRefresh}
      />
    </div>
  );
}
